import * as React from 'react';
import { useLazyQuery } from "@apollo/react-hooks";
import gql from "graphql-tag";
import { Guid } from "guid-string";
import { AsyncLoadResult, AsyncLoadOptions } from '../../../shared/abstractStore';
import { analysisTypeQuery, analysisTypeQueryVariables } from '../generated/analysisTypeQuery';
import { analysisTypeFieldsFragment } from '../models/AnalysisType';
import { mainApiConfig } from '../../../configure/mainApiConfig';

export interface AnalysisTypeAsyncLoadOptions extends AsyncLoadOptions {
    name?: string | null | undefined,
}

/**
 * Get an AnalysisType from the store.
 * @param id
 * @param options
 */
export function useAnalysisType(id: string | undefined | null, options: AnalysisTypeAsyncLoadOptions = {}): AsyncLoadResult<analysisTypeQuery> {
    // Query the data we need form the api.
    const [load, { data, loading, error, refetch }] = useLazyQuery<analysisTypeQuery, analysisTypeQueryVariables>(
        gql`
        query analysisTypeQuery ($id: ID!, $name: String) {
            model: analysisType(id: $id, name: $name) {
                ...analysisTypeFields
            }
        }

        ${analysisTypeFieldsFragment}
        `,
        {
            variables: {
                id: id ?? Guid.empty,
                name: options.name ?? '',
            },
            fetchPolicy: options.fetchPolicy ?? mainApiConfig.defaultFetchPolicy,
            pollInterval: options.pollInterval,
            onCompleted: (data) => {
                if (options.onCompleted) {
                    options.onCompleted(data);
                }
            }
        }
    );

    // Run the query (unless we are being lazy).
    React.useEffect(() => {
        if (!options.lazy) {
            load();
        }
    }, [options.lazy, load])

    // Return the results.
    const refresh = React.useCallback(async () => { !refetch ? await load() : await refetch(); }, [refetch, load]);
    return React.useMemo(() => ({
        data: data ?? ({} as any),
        refresh: refresh,
        isLoading: loading,
        errors: error
    }), [data, refresh, loading, error]);
}
